/**
 * TREATX-UAT-PROD-CONTEXT-SURFACE-01c — THE single rule for AUTOMATIC
 * workspace/context selection (Codex re-review finding, 2026-09-28).
 *
 * TreatX authority = authenticated User + selected Provider + selected
 * Role/Membership, minted ONLY by `POST /api/auth/unified/context`.
 * The frontend may PROPOSE a `workspace_id`; it never grants anything.
 *
 * Root cause this module closes: several frontend call sites treated
 * "the workspace CATALOGUE has one entry" as "the account has one
 * possible context" (`availableWorkspaces.length === 1` /
 * `providerCandidates.length === 1`). The canonical catalogue
 * (`UnifiedAuthService.WORKSPACE_CATALOG`) deliberately does NOT publish
 * a route for every assignment (`treatx_*` global roles, patient with a
 * non-global scope, `api_client`, `pending`, residual clinic roles), so a
 * single-entry catalogue can belong to a MULTI-persona account — and the
 * provider context was silently minted for it.
 *
 * Canonical proof used here (no new authority, no route→role table):
 *   * `workspaces[]` is DERIVED server-side from `role_assignments[]`,
 *     at most one catalogue entry per assignment;
 *   * therefore the catalogue represents every persona of the account
 *     ONLY when it holds as many entries as the account holds
 *     assignments;
 *   * automatic selection is allowed ONLY when that holds AND exactly
 *     one of those entries is selectable (available + `default_route`).
 * Anything else is AMBIGUOUS → no automatic selection; the caller must
 * render the explicit selection/recovery surface.
 *
 * Both inputs come from the same canonical payload
 * (`/api/auth/unified/{login,me}` or the MFA finalise response).
 */

// ---------------------------------------------------------------------------
// TREATX-UAT-PROD-CONTEXT-SURFACE-01d (Codex review #3, BLOCKER 2)
//
// `treatx_last_active_workspace` predates this policy and cannot prove
// whether its value came from an EXPLICIT user choice / server-confirmed
// context or from one of the retired heuristics (first workspace, sole
// provider, patient-first, route owner). Historical provenance is NOT
// inferable, so an UNQUALIFIED value is never trusted for automatic
// context restoration: it is cleared on first read under the new policy.
//
// A restorable selection is persisted ONLY after (1) an explicit user
// choice or a genuinely unambiguous `resolveUnambiguousWorkspace` result
// AND (2) successful server validation (or when mirroring the server's
// own `active_context`). This is UX persistence of already-validated
// intent — never an authority, never a second context SSOT. The server
// context remains canonical.
// ---------------------------------------------------------------------------
export const SELECTION_POLICY_VERSION = 'v2-2026-09-28';
const ACTIVE_WORKSPACE_KEY = 'treatx_last_active_workspace';
const PROVENANCE_KEY = 'treatx_workspace_selection_provenance';

const marker = (workspaceId) => `${SELECTION_POLICY_VERSION}:${workspaceId}`;

export function clearWorkspaceSelection() {
  try {
    localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    localStorage.removeItem(PROVENANCE_KEY);
  } catch { /* ignore */ }
}

export function persistTrustedWorkspaceSelection(workspaceId) {
  if (!workspaceId) return;
  try {
    localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspaceId);
    localStorage.setItem(PROVENANCE_KEY, marker(workspaceId));
  } catch { /* private-mode storage failure is non-fatal */ }
}

/** The persisted selection, but ONLY when its provenance is trusted under
 *  the current policy. Anything else is purged and reported as absent. */
export function readTrustedWorkspaceSelection() {
  try {
    const value = localStorage.getItem(ACTIVE_WORKSPACE_KEY);
    if (!value) return null;
    if (localStorage.getItem(PROVENANCE_KEY) !== marker(value)) {
      clearWorkspaceSelection();
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

export function selectableWorkspaces(workspaces) {
  return (Array.isArray(workspaces) ? workspaces : []).filter(
    (w) => w && w.workspace_id && w.is_available && w.default_route
  );
}

/**
 * Returns the ONE workspace that is provably the account's only
 * selectable context, or `null` when that cannot be proven.
 */
export function resolveUnambiguousWorkspace(roleAssignments, workspaces) {
  const catalogue = Array.isArray(workspaces) ? workspaces : [];
  const assignments = Array.isArray(roleAssignments) ? roleAssignments : [];
  const selectable = selectableWorkspaces(catalogue);
  if (selectable.length !== 1) return null;
  // Catalogue does not cover every persona of this account (or the
  // assignments have not hydrated yet) → cannot prove unambiguity.
  if (catalogue.length !== assignments.length) return null;
  return selectable[0];
}
